import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import { Switch } from "@/components/ui/switch";
import { 
  Camera, Droplets, User, LogOut, Upload, Lock, Mail, 
  Target, Sparkles, Sun, Moon, Save, Check, Bell, Send
} from "lucide-react";

export default function Settings() {
  const { user, token, logout, updateUser } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [updatingSkin, setUpdatingSkin] = useState(false);
  const [savingRoutineSettings, setSavingRoutineSettings] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Skin profile state
  const [skinProfile, setSkinProfile] = useState(null);
  const [availableConcerns, setAvailableConcerns] = useState([]);
  const [selectedConcerns, setSelectedConcerns] = useState([]);
  const [selectedSkinType, setSelectedSkinType] = useState(user?.skin_type || '');
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Routine settings state
  const [routineSettings, setRoutineSettings] = useState({
    maxAmProducts: 5,
    maxPmProducts: 5
  });
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: false,
    reminderTime: "19:00"
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [sendingTestReminder, setSendingTestReminder] = useState(false);

  useEffect(() => {
    fetchSkinProfile();
    fetchAvailableConcerns();
    fetchRoutineSettings();
    fetchNotificationSettings();
  }, []);

  const fetchSkinProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/skin-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSkinProfile(response.data);
      setSelectedSkinType(response.data.skin_type || '');
      setSelectedConcerns(response.data.concerns?.map(c => c.id) || []);
    } catch (error) {
      console.error("Failed to fetch skin profile:", error);
    }
  };

  const fetchAvailableConcerns = async () => {
    try {
      const response = await axios.get(`${API}/skin-concerns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableConcerns(response.data);
    } catch (error) {
      console.error("Failed to fetch concerns:", error);
    }
  };

  const fetchRoutineSettings = async () => {
    try {
      const response = await axios.get(`${API}/auth/routine-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setRoutineSettings({
          maxAmProducts: response.data.max_am_products || 5,
          maxPmProducts: response.data.max_pm_products || 5
        });
      }
    } catch (error) {
      // Settings might not exist yet, use defaults
      console.log("Using default routine settings");
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/auth/notification-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setNotificationSettings({
          enabled: response.data.notifications_enabled || false,
          reminderTime: response.data.reminder_time || "19:00"
        });
      }
    } catch (error) {
      console.log("Using default notification settings");
    }
  };

  const saveNotificationSettings = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(`${API}/auth/notification-settings`, {
        notifications_enabled: notificationSettings.enabled,
        reminder_time: notificationSettings.reminderTime
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notification settings saved!");
    } catch (error) {
      toast.error("Failed to save notification settings");
    } finally {
      setSavingNotifications(false);
    }
  };

  const sendTestReminder = async () => {
    setSendingTestReminder(true);
    try {
      const response = await axios.post(`${API}/reminders/send-test`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.sent === false) {
        toast.info(response.data.message);
      } else {
        toast.success(`Test reminder sent to ${user?.email}!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test reminder");
    } finally {
      setSendingTestReminder(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, or WebP image");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.put(`${API}/auth/profile-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      updateUser({ profile_image_url: response.data.profile_image_url });
      toast.success("Profile image updated!");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const saveSkinProfile = async () => {
    if (!selectedSkinType) {
      toast.error("Please select a skin type");
      return;
    }
    
    setUpdatingSkin(true);
    try {
      const params = new URLSearchParams();
      params.append('skin_type', selectedSkinType);
      selectedConcerns.forEach(id => params.append('concern_ids', id));
      
      await axios.put(`${API}/auth/skin-profile?${params.toString()}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      updateUser({ skin_type: selectedSkinType, profile_completed: true });
      toast.success("Skin profile updated!");
      fetchSkinProfile();
    } catch (error) {
      toast.error("Failed to update skin profile");
    } finally {
      setUpdatingSkin(false);
    }
  };

  const toggleConcern = (concernId) => {
    setSelectedConcerns(prev => {
      if (prev.includes(concernId)) {
        return prev.filter(id => id !== concernId);
      }
      if (prev.length >= 3) {
        toast.error("Maximum 3 concerns allowed");
        return prev;
      }
      return [...prev, concernId];
    });
  };

  const saveRoutineSettings = async () => {
    setSavingRoutineSettings(true);
    try {
      await axios.put(`${API}/auth/routine-settings`, {
        max_am_products: routineSettings.maxAmProducts,
        max_pm_products: routineSettings.maxPmProducts
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Routine settings saved!");
    } catch (error) {
      toast.error("Failed to save routine settings");
    } finally {
      setSavingRoutineSettings(false);
    }
  };

  const changePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully!");
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const skinTypes = [
    { value: "dry", label: "Dry", emoji: "🏜️", desc: "Often feels tight, may have flaky patches" },
    { value: "oily", label: "Oily", emoji: "✨", desc: "Shiny appearance, enlarged pores" },
    { value: "combination", label: "Combination", emoji: "⚖️", desc: "Oily T-zone, dry cheeks" },
    { value: "normal", label: "Normal", emoji: "👌", desc: "Balanced, few imperfections" },
    { value: "sensitive", label: "Sensitive", emoji: "🌸", desc: "Easily irritated, prone to redness" }
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="settings-page">
      <Navbar />
      
      <main className="container mx-auto px-6 py-8 max-w-3xl">
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-serif text-3xl text-primary mb-2">Settings</h1>
          <p className="text-muted-foreground">Manage your profile, skin assessment, and routine preferences</p>
        </div>

        <div className="space-y-6">
          {/* Account Details Section */}
          <Card className="card-product rounded-3xl animate-fade-in-up stagger-1" data-testid="account-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <User className="w-5 h-5" />
                Account Details
              </CardTitle>
              <CardDescription>Your login information and profile picture</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Avatar */}
              <div className="flex items-center gap-6">
                <div className="relative">
                  <Avatar className="w-20 h-20 border-4 border-white shadow-lg">
                    <AvatarImage src={user?.profile_image_url} />
                    <AvatarFallback className="bg-primary text-white text-xl">
                      {user?.email?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    data-testid="profile-image-input"
                  />
                  <Button
                    size="icon"
                    className="absolute -bottom-1 -right-1 rounded-full w-8 h-8 bg-primary shadow-lg"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-avatar-btn"
                  >
                    {uploading ? (
                      <Upload className="w-4 h-4 animate-pulse" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Email</span>
                  </div>
                  <p className="font-medium text-primary">{user?.email}</p>
                </div>
              </div>

              <Separator />

              {/* Password Change */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Change Password</Label>
                </div>
                <div className="grid gap-3">
                  <Input
                    type="password"
                    placeholder="Current password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    data-testid="current-password-input"
                  />
                  <Input
                    type="password"
                    placeholder="New password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    data-testid="new-password-input"
                  />
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    data-testid="confirm-password-input"
                  />
                  <Button
                    onClick={changePassword}
                    disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword}
                    className="w-full sm:w-auto"
                    data-testid="change-password-btn"
                  >
                    {changingPassword ? "Updating..." : "Update Password"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Skin Profile Section */}
          <Card className="card-product rounded-3xl animate-fade-in-up stagger-2" data-testid="skin-profile-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Droplets className="w-5 h-5" />
                Skin Profile
              </CardTitle>
              <CardDescription>
                Your skin type and concerns help us create personalized routine recommendations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Skin Type Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Skin Type</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {skinTypes.map(type => (
                    <button
                      key={type.value}
                      onClick={() => setSelectedSkinType(type.value)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        selectedSkinType === type.value 
                          ? "border-primary bg-primary/5 ring-2 ring-primary/20" 
                          : "border-border hover:border-primary/50"
                      }`}
                      data-testid={`skin-type-${type.value}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{type.emoji}</span>
                        <span className="font-medium text-primary text-sm">{type.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{type.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Skin Concerns Selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Skin Concerns</Label>
                  <span className="text-xs text-muted-foreground">{selectedConcerns.length}/3 selected</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {availableConcerns.map(concern => (
                    <button
                      key={concern.id}
                      onClick={() => toggleConcern(concern.id)}
                      className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                        selectedConcerns.includes(concern.id)
                          ? "border-primary bg-primary text-white"
                          : "border-border hover:border-primary/50 text-foreground"
                      }`}
                      data-testid={`concern-${concern.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {selectedConcerns.includes(concern.id) && <Check className="w-3 h-3 inline mr-1" />}
                      {concern.name}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                onClick={saveSkinProfile}
                disabled={updatingSkin || !selectedSkinType}
                className="w-full"
                data-testid="save-skin-profile-btn"
              >
                {updatingSkin ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Skin Profile
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Routine Settings Section */}
          <Card className="card-product rounded-3xl animate-fade-in-up stagger-3" data-testid="routine-settings-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Routine Settings
              </CardTitle>
              <CardDescription>
                Customize how many products you want in each routine
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* AM Routine Size */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sun className="w-5 h-5 text-amber-500" />
                    <Label className="text-sm font-medium">Morning Routine</Label>
                  </div>
                  <Badge variant="secondary" className="text-sm">
                    {routineSettings.maxAmProducts} products
                  </Badge>
                </div>
                <Slider
                  value={[routineSettings.maxAmProducts]}
                  onValueChange={([value]) => setRoutineSettings(prev => ({ ...prev, maxAmProducts: value }))}
                  min={3}
                  max={10}
                  step={1}
                  className="w-full"
                  data-testid="am-products-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Minimal (3)</span>
                  <span>Comprehensive (10)</span>
                </div>
              </div>

              <Separator />

              {/* PM Routine Size */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Moon className="w-5 h-5 text-indigo-500" />
                    <Label className="text-sm font-medium">Evening Routine</Label>
                  </div>
                  <Badge variant="secondary" className="text-sm">
                    {routineSettings.maxPmProducts} products
                  </Badge>
                </div>
                <Slider
                  value={[routineSettings.maxPmProducts]}
                  onValueChange={([value]) => setRoutineSettings(prev => ({ ...prev, maxPmProducts: value }))}
                  min={3}
                  max={10}
                  step={1}
                  className="w-full"
                  data-testid="pm-products-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Minimal (3)</span>
                  <span>Comprehensive (10)</span>
                </div>
              </div>

              <Button
                onClick={saveRoutineSettings}
                disabled={savingRoutineSettings}
                className="w-full"
                data-testid="save-routine-settings-btn"
              >
                {savingRoutineSettings ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Routine Settings
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Notification Settings Section */}
          <Card className="card-product rounded-3xl animate-fade-in-up stagger-4" data-testid="notification-settings-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Skincare Reminders
              </CardTitle>
              <CardDescription>
                Get email reminders for your scheduled active ingredients (retinol, AHAs, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Email Reminders</Label>
                  <p className="text-xs text-muted-foreground">
                    Receive "Tonight is Retinol Night!" style reminders
                  </p>
                </div>
                <Switch
                  checked={notificationSettings.enabled}
                  onCheckedChange={(checked) => setNotificationSettings(prev => ({ ...prev, enabled: checked }))}
                  data-testid="notifications-toggle"
                />
              </div>

              {notificationSettings.enabled && (
                <>
                  <Separator />
                  
                  {/* Reminder Time */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Reminder Time</Label>
                    <div className="flex items-center gap-3">
                      <select
                        value={notificationSettings.reminderTime}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, reminderTime: e.target.value }))}
                        className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                        data-testid="reminder-time-select"
                      >
                        <option value="17:00">5:00 PM</option>
                        <option value="18:00">6:00 PM</option>
                        <option value="19:00">7:00 PM</option>
                        <option value="20:00">8:00 PM</option>
                        <option value="21:00">9:00 PM</option>
                      </select>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      You'll receive reminders at this time on days when you have actives scheduled
                    </p>
                  </div>

                  <Separator />

                  {/* Test Reminder Button */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Test Your Reminder</Label>
                    <Button
                      variant="outline"
                      onClick={sendTestReminder}
                      disabled={sendingTestReminder}
                      className="w-full"
                      data-testid="send-test-reminder-btn"
                    >
                      {sendingTestReminder ? (
                        <>Sending...</>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Test Reminder
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      A test email will be sent to {user?.email}
                    </p>
                  </div>
                </>
              )}

              <Button
                onClick={saveNotificationSettings}
                disabled={savingNotifications}
                className="w-full"
                data-testid="save-notification-settings-btn"
              >
                {savingNotifications ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Notification Settings
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Sign Out Section */}
          <Card className="card-product rounded-3xl animate-fade-in-up stagger-4">
            <CardContent className="pt-6">
              <Button
                variant="outline"
                onClick={logout}
                className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
